import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingPage } from '@/components/ui/loading';
import { GoogleMap } from '@/components/ui/google-map';
import type { MapMarker } from '@/components/ui/google-map';
import { Building2, Activity, AlertTriangle, Plus, UserPlus } from 'lucide-react';
import { useDashboard } from '../hooks/use-dashboard';
import { getPropertyLocations } from '../api/dashboard-api';
import type { PropertyLocation } from '../api/dashboard-api';
import { getGateways } from '@/features/gateways/api/gateways-api';
import type { Gateway } from '@/types/api';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { UserRole } from '@/types/api';
import { useOrg } from '@/store/org-context';
import { StatCard } from './stat-card';
import { StatStrip } from '@/components/ui/stat-strip';
import { SystemHealthCard } from './system-health-card';
import { ActivityFeedCard } from './activity-feed-card';
import { PropertyModal } from '@/features/properties/components/property-modal';
import { AddUserModal } from '@/features/organizations/components/add-user-modal';
import { addOrgMember } from '@/features/organizations/api/organizations-api';
import { createProperty } from '@/features/properties/api/properties-api';
import { useDataCache } from '@/store/data-cache-context';
import type { CreatePropertyRequest } from '@/types/api';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const { activeOrgId } = useOrg();
  const { invalidate } = useDataCache();
  const { loading, stats, systemHealthDevices, lowBatteryCount, weakSignalCount, connectionIssuesCount, activityFeed } = useDashboard();
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [propertyLocations, setPropertyLocations] = useState<PropertyLocation[]>([]);
  const [locatedGateways, setLocatedGateways] = useState<Gateway[]>([]);

  useEffect(() => {
    getPropertyLocations().then(setPropertyLocations).catch(() => {});
    getGateways({ perPage: 200 })
      .then(r => setLocatedGateways(r.items.filter(g => g.latitude != null && g.longitude != null)))
      .catch(() => setLocatedGateways([]));
  }, [activeOrgId]);

  if (loading) return <LoadingPage />;

  const mapMarkers: MapMarker[] = [
    ...propertyLocations.map(p => ({
      id: p.id,
      lat: p.latitude,
      lng: p.longitude,
      label: p.name,
      color: p.activeAlertCount > 0 ? 'red' as const : p.offlineDeviceCount > 0 ? 'yellow' as const : 'green' as const,
      icon: 'property' as const,
    })),
    ...locatedGateways.map(g => ({
      id: `gateway-${g.id}`,
      lat: g.latitude!,
      lng: g.longitude!,
      label: g.name,
      color: 'slate' as const,
      icon: 'gateway' as const,
    })),
  ];

  const offlineCount = stats.devices - stats.activeDevices;

  return (
    <>
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Stats — slim StatStrip on mobile/tablet (matches the property
            page so QA's "less scrolling" feedback applies) and the richer
            StatCard grid on lg+ where there's room for total/trend
            metadata. The strip keeps headline numbers at the top of the
            phone viewport instead of pushing the map + activity feed off
            the first screen. */}
        <StatStrip
          className="md:hidden"
          cells={[
            {
              icon: <Building2 className="w-4 h-4" />,
              label: 'Total Properties',
              shortLabel: 'Properties',
              value: stats.properties,
              tone: 'brand',
              onClick: () => navigate('/properties'),
            },
            {
              icon: <Activity className="w-4 h-4" />,
              label: 'Active Devices',
              shortLabel: 'Devices',
              // Show "active / total" on the strip too — fits the small
              // value slot and matches the StatCard's headline number.
              value: stats.devices > 0 ? `${stats.activeDevices}/${stats.devices}` : stats.activeDevices,
              // Brand (blue) to match the desktop StatCard's default
              // variant — QA flagged the green/muted swap as an
              // unexplained color change between mobile and desktop.
              tone: 'brand',
              onClick: () => navigate('/devices'),
            },
            {
              icon: <AlertTriangle className="w-4 h-4" />,
              label: 'Active Alerts',
              shortLabel: 'Alerts',
              value: stats.activeAlerts,
              tone: stats.activeAlerts > 0 ? 'error' : 'success',
              onClick: () => navigate('/alerts'),
            },
          ]}
        />
        <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Properties"
            value={stats.properties}
            icon={<Building2 className="w-5 h-5" />}
            trend={`+${stats.propertiesThisMonth} this month`}
            trendUp
            clickable
            onClick={() => navigate('/properties')}
          />
          <StatCard
            title="Active Devices"
            value={stats.activeDevices}
            total={stats.devices}
            icon={<Activity className="w-5 h-5" />}
            trend={offlineCount > 0 ? `${offlineCount} offline` : 'All online'}
            trendUp={offlineCount === 0}
            clickable
            onClick={() => navigate('/devices')}
          />
          <StatCard
            title="Active Alerts"
            value={stats.activeAlerts}
            icon={<AlertTriangle className="w-5 h-5" />}
            variant={stats.activeAlerts > 0 ? 'error' : 'success'}
            trend={stats.activeAlerts > 0 ? 'Requires attention' : 'All clear'}
            clickable
            onClick={() => navigate('/alerts')}
          />
        </div>

        {/* Quick Actions — horizontal button row, admin/manager only */}
        {canWrite && (
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setShowPropertyModal(true)}>
              <Plus className="w-4 h-4" />
              Create Property
            </Button>
            <Button onClick={() => navigate('/setup')}>
              <Plus className="w-4 h-4" />
              Setup Device
            </Button>
            <Button onClick={() => setShowInviteModal(true)}>
              <UserPlus className="w-4 h-4" />
              Invite User
            </Button>
          </div>
        )}

        {/* Map */}
        {mapMarkers.length > 0 && (
          <Card variant="elevated" padding="none">
            <GoogleMap
              markers={mapMarkers}
              onMarkerClick={(id) => {
                if (id.startsWith('gateway-')) navigate(`/gateways/${id.slice('gateway-'.length)}`);
                else navigate(`/properties/${id}`);
              }}
              className="h-72"
            />
          </Card>
        )}

        {/* Bottom Content Grid — System Health + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SystemHealthCard
            devices={systemHealthDevices}
            lowBatteryCount={lowBatteryCount}
            weakSignalCount={weakSignalCount}
            connectionIssuesCount={connectionIssuesCount}
          />
          <ActivityFeedCard items={activityFeed} />
        </div>
      </div>
    </div>

    {showPropertyModal && (
        <PropertyModal
          property={null}
          onClose={() => setShowPropertyModal(false)}
          onSave={async (data: CreatePropertyRequest) => {
            const res = await createProperty(data);
            if (res.success && res.data) {
              setShowPropertyModal(false);
              invalidate(['properties']);
              navigate(`/properties/${res.data.id}`);
            }
          }}
        />
      )}

      <AddUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={async (email, role) => {
          await addOrgMember(activeOrgId, { email, role });
          setShowInviteModal(false);
        }}
        callerRole={user?.role}
      />
    </>
  );
}
